import {observer} from 'mobx-react-lite';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EAutocomplete,
  EAutocompleteCategoryDefinitionProps,
  EAutocompleteOption,
} from 'webapp-lib/Components';

import {InfoText} from '#/InfoText';
import {
  TranslatedAddressClassification,
  TranslatedRelationshipRole,
} from '@lexian/api/contacts-api';
import {useMasterdata} from '_/hooks/useMasterdata';
import i18n from '_/i18n';
import {AddressFieldWrapper} from '_/sharedComponents/CreateFromTemplateWizard/fill-placeholders/FillPlaceholders.styles';
import {Placeholder} from '_/store/template/template.types';
import {
  addAsteriskIfRequired,
  EFAutocomplete,
  EFTextArea,
  EFTextInput,
  notRequiredRule,
  requiredRule,
} from '_/utilities/form';
import {firstOrUndefined} from '_/utilities/listUtility';

import {PlaceholderFieldProps} from './PlaceholderFields';

enum AddressCategory {
  Default = 'DEFAULT',
  Related = 'RELATED',
  Manual = 'MANUAL',
}

const ADDRESS_CATEGORIES: EAutocompleteCategoryDefinitionProps[] = [
  {
    name: AddressCategory.Default,
    mainLabel: i18n.t(
      'common:templatePlaceholders.addressCategories.contactData'
    ),
  },
  {
    name: AddressCategory.Related,
    mainLabel: i18n.t(
      'common:templatePlaceholders.addressCategories.linkedContactData'
    ),
  },
  {
    name: AddressCategory.Manual,
    mainLabel: i18n.t('common:templatePlaceholders.addressCategories.manual'),
  },
];

const fixedAddressOptions: EAutocompleteOption[] = [
  {
    value: 'enterManually',
    displayText: i18n.t('common:templatePlaceholders.addressManuelEntry'),
    titleText: undefined,
    categoryName: AddressCategory.Manual,
    isPlaceholderOption: true,
  },
];

const UAddressPlaceholderField = ({
  placeholder,
  form,
  autofocus,
}: PlaceholderFieldProps): React.ReactElement => {
  const {t} = useTranslation('common');
  const {addressClassifications, relationshipTypes} = useMasterdata();
  const rules = placeholder.optional ? notRequiredRule : requiredRule;
  const [searchQuery, setSearchQuery] = useState<string>('');

  const options =
    firstOrUndefined(placeholder.defaultValue)?.options?.map((option) => ({
      value: option.entityId,
      displayText: option.displayName,
      titleText: getAddressTitle(option, {
        addressClassifications,
        relationshipRoles: relationshipTypes.flatMap(
          (relationshipType) => relationshipType.roles
        ),
      }),
      categoryName: getAddressCategory(option),
    })) ?? [];

  const filteredOptions = [
    ...options.filter((option) =>
      option.displayText.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    ...fixedAddressOptions,
  ];

  if (!placeholder.editFieldType) {
    return (
      <EFAutocomplete
        autoFocus={autofocus}
        categories={ADDRESS_CATEGORIES}
        control={form.control}
        defaultValue={
          placeholder.value ??
          firstOrUndefined(placeholder.defaultValue)?.entityId
        }
        label={placeholder.label}
        name={placeholder.key}
        options={options}
        rules={rules}
      />
    );
  }

  return (
    <AddressFieldWrapper>
      <EAutocomplete
        autoFocus={autofocus}
        categories={ADDRESS_CATEGORIES}
        label={addAsteriskIfRequired(rules, placeholder.label)}
        options={filteredOptions}
        value={getEAutocompleteValue(placeholder)}
        noOptionFiltering
        onBlur={() => {
          if (placeholder.identifier === '') {
            placeholder.identifier = undefined;
          }
        }}
        onChange={(event) => {
          const value = firstOrUndefined(
            placeholder.defaultValue
          )?.options?.find(
            (option) => event.target.value === option.entityId
          )?.fieldValue;
          placeholder.identifier = event.target.value;

          // Fallback to empty string is needed to clear the content in textarea.
          // On `undefined` the textarea will take the `defaultValue`
          form.setValue(
            placeholder.key,
            getFormattedAddress(value, placeholder.editFieldType)
          );
          placeholder.doNotAutoUpdate = event.target.value === 'enterManually';
        }}
        onSearchQueryChange={setSearchQuery}
      />
      <InfoText text={t('placeholders.addressMultilineInfoText')} />
      {placeholder.editFieldType === 'TEXT' ? (
        <EFTextInput
          autoFocus={autofocus}
          control={form.control}
          defaultValue={getTextDefaultValue(placeholder)}
          name={placeholder.key}
          rules={rules}
        />
      ) : null}
      {placeholder.editFieldType === 'TEXTAREA' ? (
        <EFTextArea
          autoFocus={autofocus}
          control={form.control}
          defaultValue={getTextAreaDefaultValue(placeholder)}
          minRows={3}
          name={placeholder.key}
          rules={rules}
        />
      ) : null}
    </AddressFieldWrapper>
  );
};
export const AddressPlaceholderField = observer(UAddressPlaceholderField);

const getEAutocompleteValue = (placeholder: Placeholder) => {
  if (placeholder.doNotAutoUpdate) {
    return 'enterManually';
  }
  if (placeholder.identifier === null || placeholder.identifier === undefined) {
    if (!placeholder.optional) {
      return firstOrUndefined(placeholder.defaultValue)?.entityId;
    }
    return undefined;
  }
  return placeholder.identifier;
};

const getTextAreaDefaultValue = (placeholder: Placeholder) => {
  if (placeholder.value && typeof placeholder.value === 'string') {
    return placeholder.value;
  }
  if (placeholder.optional) {
    return undefined;
  }
  return firstOrUndefined(placeholder.defaultValue)?.value;
};

const getTextDefaultValue = (placeholder: Placeholder) => {
  const value = getTextAreaDefaultValue(placeholder)?.replaceAll('\n', ', ');
  return getFormattedAddress(value, placeholder.editFieldType);
};

export const getFormattedAddress = (
  value: string | undefined,
  editFieldType: Placeholder['editFieldType']
): string => {
  if (value && editFieldType === 'TEXT') {
    return value.replaceAll('\n', ', ');
  }
  return value ?? '';
};

type Option = {
  classificationId?: string;
  relatedAddressContact?: {
    label: string;
    roleId: string;
  };
};

function getAddressCategory(option: Option) {
  return option.relatedAddressContact
    ? AddressCategory.Related
    : AddressCategory.Default;
}

function getAddressTitle(
  option: Option,
  masterData: {
    addressClassifications: TranslatedAddressClassification[];
    relationshipRoles: TranslatedRelationshipRole[];
  }
) {
  if (option.relatedAddressContact) {
    const roleName = masterData.relationshipRoles.find(
      (role) => role.id === option.relatedAddressContact?.roleId
    )?.partyName;
    return `${roleName} - ${option.relatedAddressContact.label}`;
  } else {
    return masterData.addressClassifications.find(
      (classification) => classification.id === option.classificationId
    )?.name;
  }
}
